import React, { useState, useEffect, useRef } from "react";
import { Box, VStack, Flex, Heading, HStack, Text, useColorModeValue, Button as ChakraButton, Image } from "@chakra-ui/react";
import Prescription from "../../components/patient-health/doctor-notes/prescription-page";
import { GetDoctorNotesGeneratePdf, UploadPrescription } from "../../middleware/services/visits/patient-overview/updatedoctornotes";
import moment from "moment";
import {
  extractDiagnosis,
  formatMedicalHistory,
  transformVitalData,
  calculateAge,
  formatMedicines,
  formatOrders,
  formatGeneralNotes,
  formatDieticianNotes,
  transformFetchedDocuments,
  transformInvestigationData,
  processROSData
} from "../../middleware/domains/visits/patient-health/prescriptions";
import useLoader from "../../components/loader/loaderstates";
import Loader from "../../components/loader/loader";
import { useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { formatDate_Do_MMM_YYYY, uploadImageCommonFunction } from "../../auth";
import { PrimaryButton } from "../../components";
import { InfoIcon, DownloadIcon, CheckCircleIcon } from "@chakra-ui/icons";
import processPhysicalExamData from "../../middleware/domains/visits/patient-health/physical-exam";


const GeneratePDF = () => {
  const [inputToPrescription, setInputToPrescription] = useState(null);
  const [prescriptionData, setPrescriptionData] = useState({
    patientId: "",
    doctorId: "",
    appointmentId: "",
    branchid: "",
    hospitalid: "",
    blobUrl: "",
  });
  const { isLoading, showLoader, dismissLoader } = useLoader();
  const location = useLocation();
  const prescriptionRef = useRef(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get("patientId");
    const doctorId = searchParams.get("doctorId");
    const appointmentId = searchParams.get("appointmentId");
    const branchid = searchParams.get("branchid");
    const hospitalid = searchParams.get("hospitalid");
    const isSign = searchParams.get("isSigned");

    if (patientId && doctorId && appointmentId) {
      fetchDoctorNotes(patientId, doctorId, appointmentId, branchid, hospitalid, isSign);
    }
  }, [location.search]);

  const fetchDoctorNotes = async (patientId, doctorId, appointmentId, branchid, hospitalid, isSign) => {
    const inputToFetch = {
      input: {
        patientid: patientId,
        doctorid: doctorId,
        appointmentid: appointmentId,
        branchid: branchid,
        hospitalid: hospitalid,
      },
    };

    showLoader();
    try {
      const response = await GetDoctorNotesGeneratePdf(inputToFetch.input);
      const data = response?.output?.data;
      setPrescriptionData({
        patientId: patientId,
        doctorId: doctorId,
        appointmentId: appointmentId,
        branchid: branchid,
        hospitalid: hospitalid,
        blobUrl: data?.imagepath || "",
      })

      const addressDetails = data?.info?.hospitaladdress;
      let fullAddress = '-';

      if (addressDetails) {
        const {
          address,
          address2,
          city,
          STATE,
          postalcode,
          country
        } = addressDetails;

        if (address || address2 || city || STATE || postalcode || country) {
          fullAddress = [
            address && `${address}`,
            address2 && `${address2}`,
            city && `${city}`,
            STATE && `${STATE}`,
            postalcode && `${postalcode}`,
            country && `${country}`
          ]
            .filter(Boolean)
            .join(', ');
        }
      }
      const fetchedData = {
        drName: data?.info?.drname,
        hospitalLogo: data?.info?.hospitallogo,
        doctorQualification: data?.info?.doctorqualification,
        doctorSpeciality: data?.info?.doctorspeciality,
        doctorPhone: data?.info?.doctorphone,
        doctorPhoneCountryCode: data?.info?.doctorcountrycode,
        doctorMail: data?.info?.doctormail,
        //hospitalAddress: data?.info?.hospitaladdress?.address,
        hospitalAddress: fullAddress,
        patientBookingId: data?.info?.bookingid,
        patientmrnnumber: data?.info?.patientmrn,
        patientName: data?.info?.patientname,
        patientGender: data?.info?.patientgender
          ? data.info.patientgender.charAt(0).toUpperCase() +
          data.info.patientgender.slice(1)
          : null,
        patientAge: calculateAge(data?.info?.patientage),
        patientPhone: data?.info?.patientphone,
        patientPhoneCountryCode: data?.info?.patientcountrycode,
        appointmentDate: data?.info?.appointmentdate
          ? moment(data?.info?.appointmentdate).format("D MMM YYYY")
          : "-",
        settings: data.healthviewsetting || {},
        hpi: data.historyofpresentillness|| [],
        hpitext: data?.hpitext?.description,
        ros: processROSData(data.reviewofsystem) || [],   
        vitalData: transformVitalData(data.vitals || []),
        investigationData: transformInvestigationData(data.pinvestigations || []),
        chiefcomplaint: data?.chiefcomplaint?.chiefcomplaint,
        patientdiagnosis: data?.patientdiagnosis,
        diagnosisData: extractDiagnosis(data.doctorsassessment || []),
        medicalHistory: formatMedicalHistory(data.medicalhistory || []),
        prescribedMedicines: formatMedicines(data.orders.medication || []),
        scaleOut:
          formatMedicines(data.orders.medication || []).length > 5
            ? true
            : false,
        recommendedTests: formatOrders(data.orders || []),
        followUpDate: "-",
        referralDoctor: "-",
        doctorSignImage: isSign === "true" ? data?.info?.drname : null,
        time: isSign && isSign === true ? formatDate_Do_MMM_YYYY(data?.time) : null,
        generalnotes: formatGeneralNotes(data.generalnotes || []),
        dieticiannotes: formatDieticianNotes(data.dieticiannote || []),
        documents: transformFetchedDocuments(data.documentdetail || []),
        templateid: data.templateid? data.templateid : 1,
        customtemplatevalues: data.templatevalue,
        physicalexam: processPhysicalExamData(data.physicalexam) || [],  
      };
      setInputToPrescription(fetchedData);
    } catch (error) {
      console.error("Error fetching Doctor Notes data:", error);
    } finally {
      dismissLoader();
    }
  };



  const handleDownload = async () => {
    const elements = prescriptionRef.current.querySelectorAll(".printPdf"); // Select all elements with the specified class
    const Esignature = prescriptionRef.current.querySelector(".Esignature");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      // Generate a canvas for the current element
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/jpeg", 1); // Use high-quality JPEG

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width; // Scale the height proportionally

      if (i > 0) {
        pdf.addPage(); // Add a new page for every div after the first
      }

      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
    }
    if (Esignature != null) {
      Esignature.style.marginTop = "-0.875rem"
      Esignature.style.marginTop = "0"
    }
    const pdfBlob = pdf.output("blob"); // Generate the PDF as a Blob
    return pdfBlob; // Return the Blob
  };

  const savePrescriptionUpload = async (fileName) => {
    const inputToSave = {
      input: {
        patientid: prescriptionData?.patientId,
        doctorid: prescriptionData?.doctorId,
        appointmentid: prescriptionData?.appointmentId,
        prescriptionurl: fileName,
        baseurl: prescriptionData?.blobUrl,
        doctorname: inputToPrescription.drName,
        patientname: inputToPrescription.patientName,
        patientmobile: inputToPrescription.patientPhone,
        patientmobilecountrycode: inputToPrescription.patientPhoneCountryCode
      },
    };
    try {
      const response = await UploadPrescription(inputToSave.input);
      setDownloadUrl(prescriptionData?.blobUrl + fileName || null);
      // console.log(response);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const uploadFile = async () => {
    try {
      // Create an HTML file for testing/preview
      // const htmlBlob = new Blob([content.html], { type: 'text/html' });
      // const htmlUrl = URL.createObjectURL(htmlBlob);
      // console.log('Preview HTML URL:', htmlUrl);

      const pdfBlob = await handleDownload();
      const file = new File([pdfBlob], "download.pdf", {
        type: "application/pdf",
      });
      const response = await uploadImageCommonFunction(file, "download.pdf");
      if (response) {
        savePrescriptionUpload(response.name);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  useEffect(() => {
    if (inputToPrescription && prescriptionRef.current) {
      setTimeout(() => {
        uploadFile();
      }, 500);
    }
  }, [inputToPrescription]);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Flex
      position="relative"
      minH="500px"
      align="center"
      justify="center"
      p={4}
    >
      {/* Hidden prescription for PDF generation */}
      <Box
        ref={prescriptionRef}
        position="absolute"
        left="-9999px"
        width="800px"
      >
        {inputToPrescription && <Prescription {...inputToPrescription} />}
      </Box>

      {/* Main UI */}
      <Box
        w="full"
        maxW="lg"
        mx="auto"
        boxShadow="xl"
        border="0"
        bg={cardBg}
        borderRadius="xl"
      >
        <Box
          textAlign="center"
          pb={6}
          pt={8}
          borderTopRadius="xl"
          borderBottom="1px"
          borderColor={borderColor}
        >
          <Flex
            mx="auto"
            mb={6}
            p={4}
            border='1px solid #e2e2e2'
            borderRadius="full"
            w="fit-content"
            align="center"
            justify="center"
          >
            <Image src="/assets/svgs/reports-filled.svg" boxSize='25px' />
          </Flex>
          <Heading as="h2" fontSize="2xl" fontWeight="bold" color="gray.800" mb={2}>
            Prescription Document
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {isLoading ? "Processing your prescription..." : "Your document is ready"}
          </Text>
        </Box>

        <Box textAlign="center" px={8} py={8}>
          {isLoading && (
            <VStack spacing={6}>
              <Box>
                <Loader />
              </Box>
              <VStack spacing={3}>
                <Heading as="h3" fontSize="lg" fontWeight="semibold" color="gray.800">
                  Generating Your PDF
                </Heading>
                <Text color="gray.600" lineHeight="relaxed">
                  We&apos;re processing your prescription data and converting it into a professional PDF
                  document. This usually takes a few moments.
                </Text>
                {/* Progress dots */}
                <HStack justify="center" pt={4} spacing={2}>
                  <Box
                    w={2}
                    h={2}
                    bg="blue.600"
                    borderRadius="full"
                    animation="bounce 1s infinite"
                  />
                  <Box
                    w={2}
                    h={2}
                    bg="blue.600"
                    borderRadius="full"
                    animation="bounce 1s infinite"
                    style={{ animationDelay: "150ms" }}
                  />
                  <Box
                    w={2}
                    h={2}
                    bg="blue.600"
                    borderRadius="full"
                    animation="bounce 1s infinite"
                    style={{ animationDelay: "300ms" }}
                  />
                </HStack>
              </VStack>
            </VStack>
          )}

          {!isLoading && downloadUrl && (
            <VStack spacing={6}>
              <VStack spacing={4}>
                <Flex mx="auto" w={16} h={16} bg="green.100" borderRadius="full" align="center" justify="center">
                  <Image src="/assets/imgs/success-alert.png" />
                </Flex>
                <VStack spacing={2}>
                  <Heading as="h3" fontSize="xl" fontWeight="semibold" color="gray.800">
                    PDF Ready for Download!
                  </Heading>
                  <Text color="gray.600">
                    Your prescription has been successfully generated and is ready to download.
                  </Text>
                </VStack>
              </VStack>
              {/* Download Section */}
              <Box
                bgGradient="linear(to-r, green.50, emerald.50)"
                p={6}
                borderRadius="xl"
                borderWidth="1px"
                borderColor="green.200"
              >
                <VStack spacing={4}>
                  <HStack justify="center" spacing={2} color="green.700">
                    <InfoIcon size={20} />
                    <Text fontWeight="medium">Prescription.pdf</Text>
                  </HStack>
                  <a
                    href={downloadUrl}
                    download
                    target="_blank"
                    style={{ width: "100%", display: "inline-block" }}
                    rel="noreferrer"
                  >
                    {PrimaryButton ? (
                      <PrimaryButton buttonText="View PDF" />
                    ) : (
                      <ChakraButton
                        w="full"
                        bgGradient="linear(to-r, blue.600, indigo.600)"
                        color="white"
                        fontWeight="semibold"
                        py={3}
                        px={6}
                        borderRadius="lg"
                        boxShadow="lg"
                        _hover={{
                          bgGradient: "linear(to-r, blue.700, indigo.700)",
                          transform: "scale(1.05)",
                          boxShadow: "xl",
                        }}
                        leftIcon={<DownloadIcon size={20} />}
                        transition="all 0.2s"
                      >
                        View PDF
                      </ChakraButton>
                    )}
                  </a>
                  <Text fontSize="xs" color="green.600" fontWeight="medium">
                    ✓ The PDF will open in a new tab and download automatically
                  </Text>
                </VStack>
              </Box>
            </VStack>
          )}

          {!isLoading && !downloadUrl && (
            <VStack spacing={4}>
              <Flex mx="auto" w={16} h={16} bg="gray.100" borderRadius="full" align="center" justify="center">
                <InfoIcon size={32} color="#a3a3a3" />
              </Flex>
              <VStack spacing={2}>
                <Heading as="h3" fontSize="lg" fontWeight="semibold" color="gray.800">
                  Initializing PDF Generation
                </Heading>
                <Text color="gray.600">
                  Please wait while we prepare your prescription document...
                </Text>
              </VStack>
            </VStack>
          )}
        </Box>
      </Box>
    </Flex>


    // <Box position="relative">
    //   {isLoading && <Loader />}
    //   <div
    //     ref={prescriptionRef}
    //     style={{ position: 'absolute', left: '-9999px', width: '800px' }}
    //   >
    //     {inputToPrescription && <Prescription {...inputToPrescription} />}
    //   </div>

    //   <VStack align="center">
    //     <Box mt="1rem" overflowY={"auto"}>
    //       {isLoading && <p>Please wait, your prescription is generating...</p>}
    //       {!isLoading && downloadUrl && (
    //         <a href={downloadUrl} download target="_blank">
    //           <PrimaryButton buttonText='Download PDF' />
    //         </a>
    //       )}
    //     </Box>
    //   </VStack>
    // </Box>
  );
};

export default GeneratePDF;